<template>
  <div>
    <div v-if="data">
      <div
        v-if="content.showFilterBar"
        class="sticky top-0 z-1 b-y b-grey-70 bg-white p-2"
        :class="content.hideFilterTitle ? 'pt-2' : 'pt-4'"
      >
        <div class="flex between">
          <h2 v-if="!content.hideFilterTitle" class="pr-2 subtitle-2 fw-bold">
            {{ content.filterTitle || $t.articleFilters }}
          </h2>
          <template v-if="activeFilters.length">
            <div>
              ({{ activeFilters.length }})
            </div>
            <base-button class="mr-a px-2 text-sm underlined" @click="clearAllFilters">
              {{ $t.clearAll }}
            </base-button>
          </template>
          <div>
            <base-button
              class="text-sm underlined md:hidden"
              :class="{ '<md:hidden': expandedFilters && data.facets.length >= filtersPerBreakpoint.sm }"
              @click="expandedFilters = !expandedFilters"
            >
              {{ $t.seeMore }}
            </base-button>
          </div>
        </div>

        <div class="flex py-4" :class="{ wrap: expandedFilters }">
          <client-only>
            <base-button
              v-for="(filter, i) in filtersToShow"
              :key="filter.id"
              class="py-1 pr-2 text-sm"
              :class="{ 'fw-bold underlined': activeFilters.includes(filter.id), 'pl-2': i > 0 }"
              @click="toggleFilter(filter.id)"
            >
              {{ filter.label }}
            </base-button>

            <base-button
              class="px-2 text-sm underlined"
              :class="{
                '<md:hidden': !expandedFilters,
                'md:hidden': data.facets.length <= filtersPerBreakpoint.md,
                'lg:hidden': data.facets.length <= filtersPerBreakpoint.lg,
              }"
              @click="expandedFilters = !expandedFilters"
            >
              {{ expandedFilters ? $t.seeLess : $t.seeMore }}
            </base-button>
          </client-only>
        </div>
      </div>
      <div>
        <h2 v-if="hasTitle" class="pb-6 pt-8 text-center title-1">
          <span v-if="renderedTitleHtml" v-html="renderedTitleHtml" />
          <span v-else>{{ content.title }}</span>
        </h2>
      </div>
      <base-grid :cols="content.gridSize" :gap>
        <div v-for="article in articlesToShow" :key="article.id">
          <vf-article-card
            ref="articleRefs"
            v-bind="article"
            :hide-page-date="content.hidePageDate"
            :image="{
              src: article.picture?.url,
              alt: article.picture.alt,
            }"
            :tags="[article.eyebrow]"
            :to="article.url"
          />
        </div>
      </base-grid>
      <div v-if="content.loadMore && articlesToShow.length < data?.numFound" class="py-6 text-center">
        <vf-button :loading="pending" @click="loadMore">
          {{ $t.readMore }}
        </vf-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { ArticleGridContent } from '#types/components/cms/article-grid'

const { content } = defineProps<{ content: ArticleGridContent }>()

const { $feature } = useNuxtApp()
const { gap } = useAppConfig().components.cms.articleGrid
const renderedTitleHtml = computed(() => $feature.showTitleHtml && hasTextContent(content.titleHtml) ? content.titleHtml : '')
const hasTitle = computed(() => !!renderedTitleHtml.value || hasTextContent(content.title))

const {
  data,
  activeFilters,
  expandedFilters,
  filtersPerBreakpoint,
  filtersToShow,
  clearAllFilters,
  toggleFilter,
  articlesToShow,
  pending,
  loadMore,
  articleRefs
} = useArticles({ ...content, kind: 'grid' })
</script>
